import { useAuth } from 'hooks';
import { PosFailureStatus, PosSuccessStatus, PosWebSocketMessage } from 'model/WebSockets';
import { FunctionComponent, useCallback, useEffect, useRef } from 'react';
import { useActionCableChannel } from './useActionCableChannel';
import { websocketLog } from './websocketLog';

interface PosWebSocketProps {
    invoiceId: number;
    terminalId: number;
    onMessageReceived?: (message: string, error: boolean) => void;
    onSuccess?: () => void;
    onError?: () => void;
    onGetPosServiceId?: (serviceId: string) => void;
}

const PosWebSockets: FunctionComponent<PosWebSocketProps> = props => {
    const auth = useAuth();
    const callbacksRef = useRef(props);

    useEffect(() => {
        callbacksRef.current = props;
    });

    const onReceived = useCallback(
        (webSocketMessage: PosWebSocketMessage) => {
            websocketLog('message', 'PosWebSockets', {
                status: webSocketMessage.body?.status,
                invoiceId: props.invoiceId,
                terminalId: props.terminalId
            });

            const isFailure = webSocketMessage.body.status?.toUpperCase() === PosFailureStatus;

            callbacksRef.current.onMessageReceived?.(webSocketMessage.body.message, isFailure);

            if (webSocketMessage.body.status?.toUpperCase() === PosSuccessStatus) {
                callbacksRef.current.onSuccess?.();
            } else if (isFailure) {
                callbacksRef.current.onError?.();
            }
        },
        [props.invoiceId, props.terminalId]
    );

    useActionCableChannel<PosWebSocketMessage>({
        channel: 'PosTerminalChannel',
        params: {
            marketplace_id: auth.marketplaceId,
            terminal_id: props.terminalId,
            invoice_id: props.invoiceId
        },
        enabled: Boolean(auth.signedIn && auth.marketplaceId !== undefined && props.invoiceId && props.terminalId),
        onReceived,
        subscriptionDeps: [auth.marketplaceId, props.invoiceId, props.terminalId],
        logLabel: 'PosWebSockets'
    });

    return null;
};

export default PosWebSockets;
